import React from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { Typography, Paper, Container } from '@material-ui/core';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import type { ReportSection } from './utils/reportSectionErrors';

/**
 * project_management#545 — stands in for a report section whose data failed
 * to load, so the rest of the report (and the PDF) still renders.
 */
interface SectionErrorProps {
  section: ReportSection;
  message: string;
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: 800,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderLeft: `4px solid ${theme.palette.warning.main}`,
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  message: {
    color: theme.palette.text.secondary,
  },
  hint: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
}));

export function SectionError({ section, message }: SectionErrorProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <Container className={classes.root} component={Paper} data-section-error={section}>
      <Typography className={classes.title} variant="h6">
        <ErrorOutlineIcon color="action" fontSize="small" />
        {t('report.sectionUnavailable', { section: t(`report.sections.${section}`) })}
      </Typography>
      <Typography className={classes.message} variant="body2">
        {message}
      </Typography>
      <Typography className={classes.hint} variant="caption">
        {t('report.sectionUnavailableHint')}
      </Typography>
    </Container>
  );
}
